// Calendar math in a product's own time zone. The daily triage, the weekly
// summary and measurement days all follow the product's local calendar, not
// UTC (design/plan.md §2.4).

export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string) {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(timeZone, f);
  }
  return f;
}

export function isValidTimeZone(timeZone: unknown): timeZone is string {
  if (typeof timeZone !== "string" || !timeZone || timeZone.length > 64) return false;
  try {
    formatter(timeZone);
    return true;
  } catch {
    return false;
  }
}

export interface LocalParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export function localParts(instant: number, timeZone: string): LocalParts {
  const parts: Record<string, number> = {};
  for (const p of formatter(timeZone).formatToParts(new Date(instant)))
    if (p.type !== "literal") parts[p.type] = Number(p.value);
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second,
  };
}

const pad = (n: number) => String(n).padStart(2, "0");
const dateOfParts = (p: { year: number; month: number; day: number }) =>
  `${p.year}-${pad(p.month)}-${pad(p.day)}`;

/** The calendar date (`YYYY-MM-DD`) at `instant` in `timeZone`. */
export const localDate = (instant: number, timeZone: string) => dateOfParts(localParts(instant, timeZone));

export function isDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const t = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === value;
}

/** `date` shifted by whole calendar days. */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Whole calendar days from `a` to `b`. */
export const daysBetween = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY);

/** ISO weekday of a calendar date: Monday 1 … Sunday 7. */
export function weekday(date: string): number {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay();
  return day === 0 ? 7 : day;
}

/** ISO week key such as `2026-W40` for a calendar date. */
export function isoWeekKey(date: string): string {
  const monday = addDays(date, 1 - weekday(date));
  const thursday = addDays(monday, 3);
  const year = Number(thursday.slice(0, 4));
  const jan4 = `${year}-01-04`;
  const firstMonday = addDays(jan4, 1 - weekday(jan4));
  const week = 1 + Math.round(daysBetween(firstMonday, monday) / 7);
  return `${year}-W${pad(week)}`;
}

/** Monday of the ISO week that contains `date`. */
export const weekStart = (date: string) => addDays(date, 1 - weekday(date));

/** Local wall time minus UTC at `instant`, in milliseconds. */
export function offsetAt(instant: number, timeZone: string): number {
  const p = localParts(instant, timeZone);
  const wall = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return wall - Math.floor(instant / 1000) * 1000;
}

/**
 * The instant at which the clock in `timeZone` shows `date hour:minute`.
 * Ambiguous times (clocks set back) resolve to the earlier instant; times that
 * do not exist (clocks set forward) move forward by the gap, like Temporal's
 * "compatible" disambiguation.
 */
export function zonedTime(date: string, hour: number, minute: number, timeZone: string): number {
  const [y, m, d] = date.split("-").map(Number);
  const wall = Date.UTC(y, m - 1, d, hour, minute);
  const before = wall - offsetAt(wall - 12 * HOUR, timeZone);
  const after = wall - offsetAt(wall + 12 * HOUR, timeZone);
  const matches = [before, after].filter((t) => {
    const p = localParts(t, timeZone);
    return dateOfParts(p) === date && p.hour === hour && p.minute === minute;
  });
  return matches.length ? Math.min(...matches) : before;
}

/** Midnight at the start of `date` in `timeZone`. */
export const startOfLocalDay = (date: string, timeZone: string) => zonedTime(date, 0, 0, timeZone);

/**
 * A product's week containing `date` (docs/design-system.md §0.4c): from the
 * start of the weekly run's weekday (ISO, Monday 1) to the same day a week
 * later, as local dates; `end` is the next week's `start`.
 */
export function cycleOf(date: string, runWeekday: number): { start: string; end: string } {
  const start = addDays(date, -((weekday(date) - runWeekday + 7) % 7));
  return { start, end: addDays(start, 7) };
}

export interface TriageSchedule {
  /** The product's local date at `now`. */
  local_date: string;
  /** When today's triage is (or was) due. */
  due_at: number;
  /** Whether a triage should run now. */
  due: boolean;
  /** The next instant a triage will be due, if it is not due now. */
  next_at: number;
}

/**
 * The daily triage runs once per local date, at or after `hour` local time.
 * `lastDate` is the local date of the last completed triage.
 */
export function triageSchedule(now: number, timeZone: string, hour: number, lastDate: string | null): TriageSchedule {
  const today = localDate(now, timeZone);
  const dueAt = zonedTime(today, hour, 0, timeZone);
  const due = now >= dueAt && lastDate !== today;
  const nextAt = due
    ? now
    : lastDate === today || now >= dueAt
      ? zonedTime(addDays(today, 1), hour, 0, timeZone)
      : dueAt;
  return { local_date: today, due_at: dueAt, due, next_at: nextAt };
}

/** Whether `now` falls in the local quiet hours `[start, end)`, wrapping midnight. */
export function inQuietHours(now: number, timeZone: string, start: number, end: number): boolean {
  if (start === end) return false;
  const { hour } = localParts(now, timeZone);
  return start < end ? hour >= start && hour < end : hour >= start || hour < end;
}
